import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaCoins,
  FaCheck,
  FaBan,
} from "react-icons/fa";
import apiRequest from "../services/api";

const PayrollComponentManagement = () => {
  const [components, setComponents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    type: "EARNING",
    category: "NONE",
    calculationType: "FIXED",
    taxable: false,
    pensionable: false,
    active: true,
    displayOrder: 0,
  });

  const fetchComponents = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("payrolls/components");
      setComponents(resp.data || []);
    } catch (err) {
      setError(err?.message || "Failed to fetch components.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComponents();
  }, []);

  const openModal = (mode, component = null) => {
    setModalMode(mode);
    setError("");

    if (component) {
      setFormData({
        id: component.id,
        name: component.name || "",
        type: component.type || "EARNING",
        category: component.category || "NONE",
        calculationType: component.calculationType || "FIXED",
        taxable: !!component.taxable,
        pensionable: !!component.pensionable,
        active: component.active !== false,
        displayOrder: component.displayOrder || 0,
      });
    } else {
      setFormData({
        id: null,
        name: "",
        type: "EARNING",
        category: "NONE",
        calculationType: "FIXED",
        taxable: false,
        pensionable: false,
        active: true,
        displayOrder: 0,
      });
    }

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Name is required.");
      return;
    }

    const endpoint = "payrolls/components";
    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          name: formData.name.trim(),
          category: formData.category || "NONE",
          displayOrder: parseInt(formData.displayOrder, 10) || 0,
        },
      });

      fetchComponents();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Configuration rejected.");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently remove: "${name}"?`)) return;

    try {
      await apiRequest(`payrolls/components/${id}`, {
        method: "DELETE",
      });
      fetchComponents();
    } catch (err) {
      setError(err?.message || "Component delete blocked.");
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const selectClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition appearance-none dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-200 shadow-sm rounded-2xl p-6 dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <FaCoins className="text-emerald-500" />
              Salary Structure Components
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Configure salary package details except base salary (it is already
              recorded in the employee table)
            </p>
          </div>

          <button
            onClick={() => openModal("create")}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
          >
            <FaPlus />
            New Component
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                  <th className="px-6 py-4">Component</th>
                  <th className="px-6 py-4">Type</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Calculation Type</th>
                  <th className="px-6 py-4 text-center">Taxable</th>
                  <th className="px-6 py-4 text-center">Pensionable</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-8 text-slate-400 dark:text-slate-500"
                    >
                      Loading ...
                    </td>
                  </tr>
                ) : components.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-8 text-slate-400 dark:text-slate-500"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  components.map((comp) => (
                    <tr
                      key={comp.id}
                      className="hover:bg-slate-50 transition-colors dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-6 py-4 font-semibold text-slate-800 dark:text-white">
                        <div className="flex flex-col">
                          <span>{comp.name}</span>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500">
                            Order Ref: {comp.displayOrder}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            comp.type === "EARNING"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                              : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20"
                          }`}
                        >
                          {comp.type}
                        </span>
                      </td>

                      {/* Category */}
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            comp.category === "TRANSPORT"
                              ? "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20"
                              : "bg-slate-100 text-slate-500 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-white/10"
                          }`}
                        >
                          {comp.category || "NONE"}
                        </span>
                      </td>

                      <td className="px-6 py-4 font-mono text-slate-500 dark:text-slate-400">
                        {comp.calculationType}
                      </td>

                      <td className="px-6 py-4 text-center">
                        {comp.taxable ? (
                          <FaCheck className="text-emerald-600 dark:text-emerald-400 mx-auto" />
                        ) : (
                          <FaBan className="text-slate-400 dark:text-slate-600 mx-auto" />
                        )}
                      </td>

                      <td className="px-6 py-4 text-center">
                        {comp.pensionable ? (
                          <FaCheck className="text-emerald-600 dark:text-emerald-400 mx-auto" />
                        ) : (
                          <FaBan className="text-slate-400 dark:text-slate-600 mx-auto" />
                        )}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] border ${
                            comp.active
                              ? "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20"
                              : "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-white/5"
                          }`}
                        >
                          {comp.active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openModal("view", comp)}
                            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
                            title="View"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => openModal("edit", comp)}
                            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200 rounded-lg transition dark:bg-blue-600/10 dark:text-blue-400 dark:border-blue-500/10 dark:hover:bg-blue-600/20"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(comp.id, comp.name)}
                            className="p-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition dark:bg-red-600/10 dark:text-red-400 dark:border-red-500/10 dark:hover:bg-red-600/20"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[2000] flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
            onClick={() => setIsModalOpen(false)}
          />

          <div className="relative w-full max-w-xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 sm:p-8 shadow-2xl overflow-y-auto max-h-[90vh] dark:bg-slate-900 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)]">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                  {modalMode} Component
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Label *
                </label>
                <input
                  type="text"
                  required
                  disabled={modalMode === "view"}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Transport Allowance, Representation Allowance"
                  className={inputClass}
                />
              </div>

              {/* Type + Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Type
                  </label>
                  <select
                    disabled={modalMode === "view"}
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    className={selectClass}
                  >
                    <option value="EARNING">EARNING (+)</option>
                    <option value="DEDUCTION">DEDUCTION (-)</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Category
                  </label>
                  <select
                    disabled={modalMode === "view"}
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className={selectClass}
                  >
                    <option value="NONE">NONE</option>
                    <option value="TRANSPORT">TRANSPORT</option>
                  </select>
                  {formData.category === "TRANSPORT" && (
                    <p className="text-[10px] text-blue-600 dark:text-blue-400 mt-0.5">
                      First 600 ETB is non-taxable
                    </p>
                  )}
                </div>
              </div>

              {/* Calculation */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Calculation Logic
                </label>
                <select
                  disabled={modalMode === "view"}
                  value={formData.calculationType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      calculationType: e.target.value,
                    })
                  }
                  className={selectClass}
                >
                  <option value="FIXED">FIXED VALUE</option>
                  <option value="PERCENTAGE">
                    PERCENTAGE VALUE BASED ON BASE SALARY
                  </option>
                </select>
              </div>

              {/* Display Order + Checks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Display Order
                  </label>
                  <input
                    type="number"
                    disabled={modalMode === "view"}
                    value={formData.displayOrder}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        displayOrder: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                <div className="flex flex-col justify-end gap-2 pb-1">
                  <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 select-none">
                    <input
                      type="checkbox"
                      disabled={modalMode === "view"}
                      checked={formData.taxable}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          taxable: e.target.checked,
                        })
                      }
                      className="rounded bg-slate-50 border-slate-300 text-emerald-600 focus:ring-0 dark:bg-slate-950 dark:border-white/10"
                    />
                    Taxable
                  </label>

                  <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 select-none">
                    <input
                      type="checkbox"
                      disabled={modalMode === "view"}
                      checked={formData.pensionable}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pensionable: e.target.checked,
                        })
                      }
                      className="rounded bg-slate-50 border-slate-300 text-emerald-600 focus:ring-0 dark:bg-slate-950 dark:border-white/10"
                    />
                    Pensionable
                  </label>

                  <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 select-none">
                    <input
                      type="checkbox"
                      disabled={modalMode === "view"}
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          active: e.target.checked,
                        })
                      }
                      className="rounded bg-slate-50 border-slate-300 text-emerald-600 focus:ring-0 dark:bg-slate-950 dark:border-white/10"
                    />
                    ACTIVE
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 sm:gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
                >
                  Cancel
                </button>

                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                  >
                    Save Changes
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollComponentManagement;
